// @givmo/app-charity — the way back in for someone whose company locks how they sign in (AS-WEB26):
// `/lost-company-access`, the address every console door's "Lost access to your company sign-in?"
// link opens and a lock email can name; and Board's director door's own copy of it,
// `/board/director/lost-company-access`, in Board's register (cut 1007e's fold, Board's B-F3: a
// director stays in one register from the invitation to the board, never in the console's admin
// sign-in shell).
//
// The flow is `@givmo/passkeys`' `EscapeHatchFlow`: in this console's own card (frame C1), or in
// Board's director shell, where each step's title is the page's title and the rest sits in the
// director's card. "Back to sign in" returns to the door that opened it: the `back` the link carried
// (sanitized, a same-origin path), else the console's door — or, for a director, the director door.

import type { ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Card, CardBody } from '@givmo/design-system';
import { DirectorShell } from '@givmo/board';
import { EscapeHatchFlow } from '@givmo/passkeys';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { DIRECTOR_ROUTES } from '../board/director-routes';
import { AuthShell } from './auth-shell';
import { AUTH_ROUTES } from './auth-routes';

/** This console's door heading, so the flow reads as that door's next screen. */
const HEADING = 'font-sans text-s22 font-w600 tracking-givmo text-text-strong';

export interface LostCompanyAccessScreenProps {
  /** `director`: Board's director door opened it, so it wears Board's register. */
  arrival?: 'console' | 'director';
}

/** Board's director register: the step's title as the page's, the rest in the director's card. */
function directorFrame(title: ReactNode, body: ReactNode) {
  return (
    <DirectorShell title={title} testId="page-director-lost-company-access">
      <Card className="w-full max-w-[440px]" testId="director-lost-company-access">
        <CardBody className="flex flex-col gap-20">{body}</CardBody>
      </Card>
    </DirectorShell>
  );
}

export function LostCompanyAccessScreen({
  arrival = 'console',
}: LostCompanyAccessScreenProps = {}) {
  const navigate = useNavigate();
  // Two routes render this screen, so the search read is non-strict by necessity.
  const { back } = useSearch({ strict: false }) as { back?: string };
  const director = arrival === 'director';
  const door =
    sanitizeRedirectTarget(back) ?? (director ? DIRECTOR_ROUTES.signIn : AUTH_ROUTES.loginHome);
  const flow = (
    <EscapeHatchFlow
      headingClass={HEADING}
      onBackToSignIn={() => void navigate({ to: door })}
      frame={director ? directorFrame : undefined}
    />
  );
  if (director) return flow;
  return <AuthShell testId="page-lost-company-access">{flow}</AuthShell>;
}
